/**
 * Checkout order submission service — pure async functions extracted from useCheckout.
 * No React hooks. Accepts all dependencies as parameters.
 *
 * Exports: submitOrder
 */
import axios from "axios";
import { toast } from "sonner";
import { trackPaymentLink } from "@/utils/tracking";

export function _getCheckoutErrorMessage(err, fallback = "Erro ao enviar pedido. Tente novamente.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message.trim()) return detail.message;
    if (typeof detail.error === "string" && detail.error.trim()) return detail.error;
  }
  if (typeof err?.message === "string" && err.message.trim()) return err.message;
  return fallback;
}

function _getFbCookies() {
  if (typeof document === "undefined") return {};
  const get = (name) => {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? m[1] : undefined;
  };
  return { fbp: get("_fbp"), fbc: get("_fbc") };
}

export function _getPublicOrderPaymentPolicy({ isTableOrder, tableSession, paymentMethod }) {
  // NFC payment-at-table belongs to the attendant flow after the bill is
  // requested. A customer's QR table order is never an online checkout.
  return {
    useTableSession: Boolean(isTableOrder && tableSession?.session_id),
    effectivePaymentMethod: isTableOrder ? "pending" : paymentMethod,
  };
}

export async function _submitTableSessionOrder({
  tableSession, cart, orderNotes, API, fmt,
  setCart, setOrderNotes, setShowCheckout, refreshTableSession, setSubmitting,
}) {
  if (!tableSession?.session_id || cart.length === 0) return;
  setSubmitting(true);
  try {
    const items = cart.map(i => ({
      product_id: i.isCombo ? null : i.id,
      combo_id: i.isCombo ? i.id : null,
      is_combo: i.isCombo || false,
      product_name: i.selectedVariations?.length > 0 ? (i.name || '').replace(/\s*\([^)]+\)$/, '') : i.name,
      quantity: i.quantity,
      unit_price: i.price,
      selected_variations: (i.selectedVariations || []).map(v => ({ name: v.name, quantity: v.quantity || 1, price: v.price || 0, group: v.group || '' })),
    }));
    const res = await axios.post(`${API}/public/table-session/${tableSession.session_id}/add-order`, {
      items,
      notes: orderNotes,
      customer_identity_provided: true,
      customer_name: tableSession.customer_name ?? "",
      customer_phone: tableSession.customer_phone ?? "",
    });
    setCart([]);
    setOrderNotes("");
    setShowCheckout(false);
    toast.success(`Pedido enviado! Total da mesa: ${fmt(res.data.session_total)}`);
    await refreshTableSession();
  } catch (err) {
    toast.error(_getCheckoutErrorMessage(err, "Erro ao enviar pedido"));
  }
  setSubmitting(false);
}

export async function submitOrder({
  cart, isTableOrder, orderType, deliveryAddress, addressNumber, addressComplement, addressBarrio,
  customerName, customerPhone,
  fullAddress, deliveryLat, deliveryLng, scheduleDate, scheduleTime,
  schedulingMode, isOpen, paymentMethod, changeFor, orderNotes,
  couponCode, loyaltyReward, influencerCode, tableNumber,
  tableSession, restaurant, restaurantId, API, fmt,
  setSubmitting, setOrderConfirmed, setConfirmedCart, setCart,
  setShowCheckout, setCheckoutStep, setOrderNotes, refreshTableSession, saveCustomerLocal,
  fbclid = "", fbc = "", fbp = "", utm_source = "", utm_medium = "", utm_campaign = "", utm_content = "", utm_term = "",
}) {
  const paymentPolicy = _getPublicOrderPaymentPolicy({ isTableOrder, tableSession, paymentMethod });
  if (paymentPolicy.useTableSession) {
    return _submitTableSessionOrder({ tableSession, cart, orderNotes, API, fmt, setCart, setOrderNotes, setShowCheckout, refreshTableSession, setSubmitting });
  }
  if (cart.length === 0) return;
  if (isTableOrder && new Date().getDay() === 3 && loyaltyReward?.id) { toast.error("Pontos e recompensas não podem ser usados em pedidos de mesa às quartas-feiras."); return; }
  if (isTableOrder && new Date().getDay() === 3 && loyaltyReward?.id) { toast.error("Pontos e recompensas não podem ser usados em pedidos de mesa às quartas-feiras."); return; }
  if (!isTableOrder && orderType === "delivery" && !deliveryAddress) { toast.error("Informe o endereco de entrega"); return; }
  if (!isTableOrder && orderType === "delivery" && !customerPhone) { toast.error("Informe seu telefone para entrega"); return; }
  if (!customerName) { toast.error("Informe seu nome"); return; }
  if (isTableOrder && !customerPhone) { toast.error("Informe seu telefone"); return; }

  const forceSchedule = schedulingMode === "forced" || (!isOpen && !isTableOrder);
  if (forceSchedule && schedulingMode !== "disabled") {
    if (!scheduleDate || scheduleDate === "asap") { toast.error("Selecione uma data para o agendamento"); return; }
    if (!scheduleTime) { toast.error("Selecione um horario para o agendamento"); return; }
  }
  if (scheduleDate && scheduleDate !== "asap" && !scheduleTime) { toast.error("Selecione um horario para o agendamento"); return; }

  const effectivePaymentMethod = paymentPolicy.effectivePaymentMethod;
  if (effectivePaymentMethod === "online") {
    const hasGateways = (restaurant?.payment_gateways || []).length > 0;
    const kaisopayReady = restaurant?.kaisopay_enabled && restaurant?.kaisopay_onboarding_complete !== false;
    if (!hasGateways && !kaisopayReady) { toast.error("Pagamento online indisponivel no momento. Tente outro metodo de pagamento."); return; }
  }

  setSubmitting(true);
  try {
    const notes = [orderNotes, effectivePaymentMethod === "cash" && changeFor ? `Troco para: ${changeFor}EUR` : ""].filter(Boolean).join(" | ");
    const phonePrefix = restaurant?.phone_prefix || (restaurant?.country_code === "PT" ? "+351" : restaurant?.country_code === "BR" ? "+55" : "+34");
    const orderData = {
      restaurant_id: restaurant?.id || restaurantId,
      table_number: tableNumber || null,
      customer_name: customerName,
      customer_phone: customerPhone ? `${phonePrefix}${customerPhone.replace(/\D/g, "")}` : "",
      customer_avatar_url: localStorage.getItem(`customer_avatar_${restaurantId}`) || null,
      delivery_address: orderType === "delivery" ? fullAddress : "",
      delivery_address_street: orderType === "delivery" ? deliveryAddress : "",
      address_number: orderType === "delivery" ? addressNumber : "",
      address_complement: orderType === "delivery" ? addressComplement : "",
      address_barrio: orderType === "delivery" ? addressBarrio : "",
      delivery_lat: orderType === "delivery" ? deliveryLat : null,
      delivery_lng: orderType === "delivery" ? deliveryLng : null,
      notes,
      order_type: isTableOrder ? "table" : orderType,
      items: cart.map(i => ({
        product_id: i.isCombo ? null : i.id,
        combo_id: i.isCombo ? i.id : null,
        is_combo: i.isCombo || false,
        product_name: i.selectedVariations?.length > 0 ? (i.name || '').replace(/\s*\([^)]+\)$/, '') : i.name,
        quantity: i.quantity,
        unit_price: i.price,
        selected_variations: (i.selectedVariations || []).map(v => ({ name: v.name, quantity: v.quantity || 1, price: v.price || 0, group: v.group || '' })),
      })),
      scheduled_for: scheduleDate !== "asap" && scheduleTime ? `${scheduleDate} ${scheduleTime.split("-")[0]}` : null,
      payment_method: effectivePaymentMethod,
      coupon_code: couponCode || null,
      referral_code: couponCode?.toUpperCase().startsWith("KAISO") ? couponCode : null,
      loyalty_reward_id: loyaltyReward?.id || null,
      influencer_code: influencerCode || null,
      // Discounts are authoritative on the server. The client sends only codes,
      // never a percentage or monetary discount amount.
      // Attribution parameters (URL takes priority over cookies)
      fbclid: fbclid || null,
      fbc: fbc || _getFbCookies().fbc || null,
      fbp: fbp || _getFbCookies().fbp || null,
      utm_source: utm_source || null,
      utm_medium: utm_medium || null,
      utm_campaign: utm_campaign || null,
      utm_content: utm_content || null,
      utm_term: utm_term || null,
    };
    setConfirmedCart([...cart]);
    const idempotencyKey = crypto.randomUUID();
    const res = await axios.post(`${API}/public/order`, orderData, { headers: { "X-Idempotency-Key": idempotencyKey } });

    const orderId = res.data.order_id || res.data.id;
    if (effectivePaymentMethod === "online" && orderId) {
      const gateways = restaurant?.payment_gateways || [];
      const activeGw = restaurant?.active_payment_gateway;
      const preferredGateway = (activeGw && gateways.includes(activeGw)) ? activeGw
        : gateways.includes("redsys") ? "redsys"
        : gateways.includes("viva") ? "viva"
        : gateways.includes("revolut") ? "revolut"
        : "stripe";
      try {
        const checkoutRes = await axios.post(`${API}/public/checkout/${orderId}`, {
          success_url: `${window.location.origin}/m/${restaurantId}?order_confirmed=${orderId}&paid=true`,
          cancel_url: `${window.location.origin}/m/${restaurantId}?order_cancelled=${orderId}`,
          gateway: preferredGateway,
        });
        trackPaymentLink({ cart_value: cart.reduce((s, i) => s + i.price * i.quantity, 0), metadata: { gateway: preferredGateway, order_id: orderId } });
        if (checkoutRes.data.gateway === "redsys" && checkoutRes.data.form_data) {
          const { form_data, action_url } = checkoutRes.data;
          const form = document.createElement("form");
          form.method = "POST";
          form.action = action_url;
          Object.entries(form_data).forEach(([name, value]) => {
            const input = document.createElement("input");
            input.type = "hidden"; input.name = name; input.value = value;
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
          return;
        }
        if (checkoutRes.data.checkout_url) { window.location.href = checkoutRes.data.checkout_url; return; }
      } catch (e) {
        if (preferredGateway === "viva" && gateways.includes("stripe")) {
          try {
            const fallbackRes = await axios.post(`${API}/public/checkout/${orderId}`, {
              success_url: `${window.location.origin}/m/${restaurantId}?order_confirmed=${orderId}&paid=true`,
              cancel_url: `${window.location.origin}/m/${restaurantId}?order_cancelled=${orderId}`,
              gateway: "stripe",
            });
            if (fallbackRes.data.checkout_url) { window.location.href = fallbackRes.data.checkout_url; return; }
          } catch {}
        }
        try { await axios.patch(`${API}/public/order/${orderId}/cancel`); } catch {}
        toast.error("Pagamento online indisponivel no momento. Tente outro metodo de pagamento.", { duration: 6000 });
        setSubmitting(false);
        return;
      }
    }

    setOrderConfirmed({ ...res.data, customer_name: customerName, customer_phone: customerPhone, wait_time_min: res.data.wait_time_min || restaurant?.wait_time_min || 30, wait_time_max: res.data.wait_time_max || restaurant?.wait_time_max || 50 });
    saveCustomerLocal(customerPhone, customerName);
    setCart([]);
    setShowCheckout(false);
    setCheckoutStep(1);
  } catch (err) {
    toast.error(_getCheckoutErrorMessage(err));
  }
  setSubmitting(false);
}
